import { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";
import "@/App.css";
import { Copy, X, ExternalLink, Twitter, Send, BarChart3, Wallet, Check, RefreshCw, Volume2, VolumeX, Fish, Zap, HelpCircle, Folder } from "lucide-react";

// Constants
const CONTRACT_ADDRESS = "63Z3Q7JX3SBGDiiwqqnPTVvHcuUk6ixkzsYQbKzhpump";
const DEX_API = "https://api.dexscreener.com/latest/dex/pairs/solana/6jqjefzcisqokmjnieg3ywfocdmztuztd6gxlr9gnugk";
const SOCIALS = {
  twitter: "https://x.com/xgreenlandshark",
  telegram: "https://t.me/TheGreenlandShark",
  dexscreener: "https://dexscreener.com/solana/6jqjefzcisqokmjnieg3ywfocdmztuztd6gxlr9gnugk",
  raydium: "https://raydium.io/swap/?inputMint=sol&outputMint=63Z3Q7JX3SBGDiiwqqnPTVvHcuUk6ixkzsYQbKzhpump",
  jupiter: "https://jup.ag/swap/SOL-63Z3Q7JX3SBGDiiwqqnPTVvHcuUk6ixkzsYQbKzhpump"
};

const IMAGES = {
  logo: "/images/logo.jpg",
  memeLore: "/images/meme1.jpg",
  memeNetflix: "/images/meme2.jpg",
  memeBsod: "/images/meme3.jpg",
  memeForum: "/images/meme4.jpg"
};

// Easter Egg Messages
const EASTER_EGGS = [
  "The shark has been watching you... for 399 years.",
  "$SHARK whispers: 'Diamond fins never rust'",
  "Secret unlocked: You found the ancient wisdom!",
  "The Greenland Shark approves your dedication.",
  "HODL level: LEGENDARY",
  "Fun fact: Greenland sharks are basically blind but still swim. Like crypto traders.",
  "You've been blessed by the 399-year-old chad.",
  "The deep lore accepts you as one of its own.",
  "$SHARK says: 'Patience young padawan, 399 years I have waited'",
  "ERROR 404: Paper hands not found in this user"
];

// Audio Context for Sound Effects
const AudioContext = createContext(null);

const useAudio = () => useContext(AudioContext);

// Sound Effect Hook
const useSoundEffects = () => {
  const audioContextRef = useRef(null);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  const playClick = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = initAudio();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.setValueAtTime(800, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.1);
    } catch (e) {
      console.log('Audio not supported');
    }
  }, [soundEnabled, initAudio]);

  const playSuccess = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = initAudio();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.setValueAtTime(523, ctx.currentTime);
      oscillator.frequency.setValueAtTime(659, ctx.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(784, ctx.currentTime + 0.2);
      
      gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch (e) {
      console.log('Audio not supported');
    }
  }, [soundEnabled, initAudio]);

  const playError = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = initAudio();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.type = 'square';
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.setValueAtTime(200, ctx.currentTime);
      oscillator.frequency.setValueAtTime(150, ctx.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.08, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.2);
    } catch (e) {
      console.log('Audio not supported');
    }
  }, [soundEnabled, initAudio]);

  const playEasterEgg = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = initAudio();
      // Play a little melody
      const notes = [523, 587, 659, 784, 880, 784, 659];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.1);
        gain.gain.setValueAtTime(0.05, ctx.currentTime + i * 0.1);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.1 + 0.1);
        osc.start(ctx.currentTime + i * 0.1);
        osc.stop(ctx.currentTime + i * 0.1 + 0.1);
      });
    } catch (e) {
      console.log('Audio not supported');
    }
  }, [soundEnabled, initAudio]);

  return { playClick, playSuccess, playError, playEasterEgg, soundEnabled, setSoundEnabled };
};

// Background Music Component
const BackgroundMusic = ({ isPlaying, volume = 0.1 }) => {
  const audioContextRef = useRef(null);
  const intervalRef = useRef(null);
  const isPlayingRef = useRef(false);

  useEffect(() => {
    // Clean up function
    const cleanup = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    if (isPlaying && !isPlayingRef.current) {
      isPlayingRef.current = true;
      try {
        // Create new context only if needed
        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        
        const ctx = audioContextRef.current;
        
        // Resume if suspended
        if (ctx.state === 'suspended') {
          ctx.resume();
        }
        
        // Create underwater ambient sound
        const playAmbient = () => {
          if (!audioContextRef.current || audioContextRef.current.state === 'closed') return;
          
          try {
            const ctx = audioContextRef.current;
            
            // Low frequency hum with wobble
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            const filter = ctx.createBiquadFilter();
            
            osc.type = 'sine';
            osc.frequency.setValueAtTime(80 + Math.random() * 40, ctx.currentTime);
            
            filter.type = 'lowpass';
            filter.frequency.setValueAtTime(200, ctx.currentTime);
            
            gain.gain.setValueAtTime(volume * 0.3, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 4);
            
            osc.connect(filter);
            filter.connect(gain);
            gain.connect(ctx.destination);
            
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 4);
            
            // Bubble sounds
            if (Math.random() > 0.5) {
              const bubble = ctx.createOscillator();
              const bubbleGain = ctx.createGain();
              bubble.type = 'sine';
              bubble.frequency.setValueAtTime(800 + Math.random() * 400, ctx.currentTime);
              bubble.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.3);
              bubbleGain.gain.setValueAtTime(volume * 0.1, ctx.currentTime);
              bubbleGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
              bubble.connect(bubbleGain);
              bubbleGain.connect(ctx.destination);
              bubble.start(ctx.currentTime + Math.random() * 2);
              bubble.stop(ctx.currentTime + 2.3);
            }
          } catch (e) {
            console.log('Ambient sound error:', e);
          }
        };
        
        playAmbient();
        intervalRef.current = setInterval(playAmbient, 3000);
      } catch (e) {
        console.log('Audio not supported');
      }
    } else if (!isPlaying && isPlayingRef.current) {
      isPlayingRef.current = false;
      cleanup();
      
      // Suspend context instead of closing to allow reuse
      if (audioContextRef.current && audioContextRef.current.state === 'running') {
        audioContextRef.current.suspend();
      }
    }
    
    return cleanup;
  }, [isPlaying, volume]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  return null;
};

// Easter Egg Toast Component
const EasterEggToast = ({ message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[9999] animate-bounce">
      <div className="win-window max-w-sm">
        <div className="win-titlebar">
          <span>SECRET DISCOVERED!</span>
          <button className="win-titlebar-btn" onClick={onClose}>
            <X size={8} />
          </button>
        </div>
        <div className="win-content text-center p-4">
          <Fish className="mx-auto mb-2 text-blue-600" size={32} />
          <p className="font-terminal text-sm">{message}</p>
        </div>
      </div>
    </div>
  );
};

// Win95 Window Component (simplified - no min/max buttons)
const Win95Window = ({ title, children, className = "", onClose }) => {
  const { playClick } = useAudio() || {};
  
  const handleClose = () => {
    playClick?.();
    onClose?.();
  };

  return (
    <div className={`win-window ${className}`} data-testid={`window-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="win-titlebar">
        <span className="truncate">{title}</span>
        {onClose && (
          <button className="win-titlebar-btn" onClick={handleClose} data-testid="close-btn">
            <X size={8} />
          </button>
        )}
      </div>
      <div className="win-content">
        {children}
      </div>
    </div>
  );
};

// Retro Button Component with Sound
const RetroButton = ({ children, onClick, className = "", href, target, ...props }) => {
  const { playClick } = useAudio() || {};
  
  const handleClick = (e) => {
    playClick?.();
    onClick?.(e);
  };

  if (href) {
    return (
      <a 
        href={href} 
        target={target} 
        rel="noopener noreferrer"
        className={`win-button inline-block text-center no-underline ${className}`}
        onClick={playClick}
        {...props}
      >
        {children}
      </a>
    );
  }
  return (
    <button className={`win-button ${className}`} onClick={handleClick} {...props}>
      {children}
    </button>
  );
};

// Start Menu Component
const StartMenu = ({ isOpen, onClose, scrollToSection, onOpenAbout }) => {
  const { playClick } = useAudio() || {};
  
  if (!isOpen) return null;

  const menuItems = [
    { icon: <Fish size={16} />, label: "About $SHARK", action: () => { scrollToSection('about'); onClose(); } },
    { icon: <Zap size={16} />, label: "Tokenomics", action: () => { scrollToSection('tokenomics'); onClose(); } },
    { icon: <Wallet size={16} />, label: "How to Buy", action: () => { scrollToSection('howtobuy'); onClose(); } },
    { icon: <Folder size={16} />, label: "Meme Vault", action: () => { scrollToSection('memes'); onClose(); } },
    { divider: true },
    { icon: <Twitter size={16} />, label: "Twitter", action: () => { window.open(SOCIALS.twitter, '_blank'); onClose(); } },
    { icon: <Send size={16} />, label: "Telegram", action: () => { window.open(SOCIALS.telegram, '_blank'); onClose(); } },
    { icon: <BarChart3 size={16} />, label: "Chart", action: () => { window.open(SOCIALS.dexscreener, '_blank'); onClose(); } },
    { divider: true },
    { icon: <HelpCircle size={16} />, label: "About", action: () => { onOpenAbout(); onClose(); } },
  ];

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="fixed bottom-16 md:bottom-14 left-2 z-50 win-window w-52 md:w-56 max-h-[70vh] overflow-hidden" data-testid="start-menu">
        <div className="win-titlebar py-1">
          <span className="text-xs">$SHARK</span>
        </div>
        <div className="bg-gray-200 max-h-[calc(70vh-30px)] overflow-y-auto">
          {/* Menu Items */}
          <div className="py-1">
            {menuItems.map((item, index) => (
              item.divider ? (
                <div key={index} className="border-t border-gray-400 my-1" />
              ) : (
                <button
                  key={index}
                  onClick={() => { playClick?.(); item.action(); }}
                  className="w-full px-3 py-2 flex items-center gap-3 hover:bg-blue-600 hover:text-white text-left font-terminal text-xs md:text-sm"
                  data-testid={`menu-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              )
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

// About Dialog
const AboutDialog = ({ isOpen, onClose }) => {
  const [clickCount, setClickCount] = useState(0);
  const { playEasterEgg } = useAudio() || {};
  
  if (!isOpen) return null;

  const handleLogoClick = () => {
    setClickCount(prev => {
      const newCount = prev + 1;
      if (newCount >= 5) {
        playEasterEgg?.();
        return 0;
      }
      return newCount;
    });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50">
      <Win95Window title="About $SHARK" onClose={onClose} className="max-w-md w-full">
        <div className="text-center p-4">
          <img 
            src={IMAGES.logo} 
            alt="$SHARK" 
            className="w-24 h-24 mx-auto mb-4 cursor-pointer hover:scale-110 transition-transform"
            onClick={handleLogoClick}
          />
          <h3 className="font-pixel text-lg mb-2">$SHARK</h3>
          <p className="font-terminal text-sm mb-2">Version 3.99</p>
          <p className="font-terminal text-xs text-gray-600 mb-4">
            The oldest meme token on Solana.<br/>
            Swimming since 1626.
          </p>
          <div className="border-t border-gray-400 pt-4 mt-4">
            <p className="font-terminal text-xs">
              Contract: {CONTRACT_ADDRESS.slice(0, 10)}...{CONTRACT_ADDRESS.slice(-8)}
            </p>
            <p className="font-terminal text-xs mt-2 text-gray-500">
              {clickCount > 0 && clickCount < 5 && `${5 - clickCount} more clicks...`}
            </p>
          </div>
          <RetroButton onClick={onClose} className="mt-4">
            OK
          </RetroButton>
        </div>
      </Win95Window>
    </div>
  );
};

// Price Ticker Component
const PriceTicker = ({ tokenData, loading }) => {
  const formatPrice = (price) => {
    if (!price) return "$0.00";
    const num = parseFloat(price);
    if (num < 0.00001) return `$${num.toExponential(2)}`;
    if (num < 1) return `$${num.toFixed(6)}`;
    return `$${num.toFixed(2)}`;
  };

  const formatMarketCap = (mc) => {
    if (!mc) return "$0";
    const num = parseFloat(mc);
    if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
    return `$${num.toFixed(0)}`;
  };

  return (
    <Win95Window title="$SHARK TRACKER" className="ticker-window">
      <div className="bg-black p-3">
        {loading ? (
          <div className="text-center">
            <RefreshCw className="animate-spin text-green-400 mx-auto" size={24} />
            <p className="text-green-400 font-terminal text-xs mt-2">LOADING...</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-center">
              <p className="text-gray-500 text-xs font-terminal">PRICE</p>
              <p className="neon-green font-pixel text-sm" data-testid="token-price">
                {formatPrice(tokenData?.priceUsd)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-gray-500 text-xs font-terminal">MARKET CAP</p>
              <p className="neon-purple font-pixel text-sm" data-testid="market-cap">
                {formatMarketCap(tokenData?.marketCap)}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 text-center">
              <div>
                <p className="text-gray-500 text-xs font-terminal">24H</p>
                <p className={`font-pixel text-xs ${tokenData?.priceChange?.h24 >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="price-change">
                  {tokenData?.priceChange?.h24 ? `${tokenData.priceChange.h24.toFixed(1)}%` : '0%'}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs font-terminal">VOL</p>
                <p className="text-yellow-400 font-pixel text-xs" data-testid="volume">
                  {formatMarketCap(tokenData?.volume?.h24)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Win95Window>
  );
};

// Hero Section with Easter Egg
const HeroSection = ({ scrollToSection, onEasterEgg }) => {
  const [copied, setCopied] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const { playSuccess, playClick } = useAudio() || {};

  const copyAddress = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(CONTRACT_ADDRESS);
      setCopied(true);
      playSuccess?.();
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [playSuccess]);

  const handleTitleClick = () => {
    playClick?.();
    setClickCount(prev => {
      const newCount = prev + 1;
      if (newCount >= 7) {
        onEasterEgg();
        return 0;
      }
      return newCount;
    });
  };

  return (
    <section className="hero-section relative" data-testid="hero-section">
      {/* Decorative Elements */}
      <div className="absolute top-10 left-10 opacity-20 hidden md:block">
        <img src={IMAGES.logo} alt="" className="w-24 h-24 spin-slow" />
      </div>
      
      {/* Main Alert Popup */}
      <div className="popup-alert shake-hover">
        <Win95Window title="SYSTEM ALERT - ANCIENT SHARK DETECTED">
          <div className="text-center space-y-4">
            <div className="bg-red-600 text-white py-2 px-4 font-pixel text-xs animate-pulse">
              WARNING: 399 YEAR OLD SHARK ON SOLANA
            </div>
            
            <img 
              src={IMAGES.memeNetflix} 
              alt="$SHARK Netflix Documentary" 
              className="w-full max-w-md mx-auto border-4 border-black"
              data-testid="hero-image"
            />
            
            <h1 
              className="font-pixel text-xl md:text-2xl text-black leading-relaxed glitch-text cursor-pointer"
              onClick={handleTitleClick}
              title={clickCount > 0 ? `${7 - clickCount} more clicks for secret...` : ''}
            >
              $SHARK
            </h1>
            <p className="font-terminal text-lg text-gray-700">
              &quot;Too Old for Ethereum, Born for Solana&quot;
            </p>
            
            {/* Contract Address */}
            <div className="contract-box">
              <span className="font-pixel text-xs text-green-400">CA:</span>
              <span className="contract-address" data-testid="contract-address">
                {CONTRACT_ADDRESS}
              </span>
              <RetroButton 
                onClick={copyAddress} 
                className={copied ? 'copy-success' : ''}
                data-testid="copy-address-btn"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'COPIED!' : 'COPY'}
              </RetroButton>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-3 mt-4">
              <RetroButton 
                href={SOCIALS.raydium} 
                target="_blank"
                className="bg-green-500 hover:bg-green-400"
                data-testid="buy-btn"
              >
                BUY $SHARK
              </RetroButton>
              <RetroButton 
                href={SOCIALS.dexscreener} 
                target="_blank"
                data-testid="chart-btn"
              >
                <BarChart3 size={14} className="inline mr-1" />
                CHART
              </RetroButton>
              <RetroButton onClick={() => scrollToSection('about')} data-testid="about-btn">
                THE LORE
              </RetroButton>
            </div>
          </div>
        </Win95Window>
      </div>
    </section>
  );
};

// Marquee Banner
const MarqueeBanner = () => {
  return (
    <div className="marquee-container">
      <div className="marquee-text text-sm">
        SWIMMING FOR 399 YEARS AND COUNTING *** DIAMOND FINS SINCE 1626 *** SLOW BUT NEVER RUGGED *** THE ULTIMATE HODLER *** SWIMMING IN THE ABYSS OF GAINS *** $SHARK CANNOT BE STOPPED *** 
      </div>
    </div>
  );
};

// About Section (Notepad Style)
const AboutSection = ({ onEasterEgg }) => {
  const [secretClicks, setSecretClicks] = useState(0);
  const { playClick } = useAudio() || {};

  const handleSecretClick = () => {
    playClick?.();
    setSecretClicks(prev => {
      if (prev >= 4) {
        onEasterEgg();
        return 0;
      }
      return prev + 1;
    });
  };

  return (
    <section id="about" className="section-spacing bg-teal-900" data-testid="about-section">
      <div className="max-w-4xl mx-auto px-4">
        <Win95Window title="notepad.exe - SHARK_LORE.txt" className="notepad-window">
          <div className="notepad-content">
            <h2 className="font-pixel text-base md:text-lg mb-4 text-center">*** THE GREENLAND SHARK MANIFESTO ***</h2>
            
            <p className="mb-4 text-sm md:text-base">
              &gt;&gt; LOADING ANCIENT WISDOM...<br/>
              &gt;&gt; SPECIES: Somniosus microcephalus<br/>
              &gt;&gt; AGE: <span className="cursor-pointer hover:text-blue-600" onClick={handleSecretClick}>399 YEARS</span> (and counting)<br/>
              &gt;&gt; STATUS: STILL SWIMMING<br/>
            </p>
            
            <p className="mb-4 text-sm md:text-base">
              While you paper-handed your Bitcoin at $100, the Greenland Shark was already 
              399 years into the swim. These absolute CHADS are the longest-living vertebrates on Earth.
            </p>
            
            <p className="mb-4 text-center font-bold text-sm md:text-base">
              &quot;I&apos;ve been HODLing since the Pilgrims landed.&quot;<br/>
              &quot;I was accumulating when Galileo was getting canceled.&quot;<br/>
              &quot;399 years and counting. The swim never stops.&quot;
            </p>
            
            <div className="border-t-2 border-b-2 border-black py-4 my-4">
              <p className="text-center text-sm md:text-base">
                Moving at just 1 MPH, I don&apos;t chase pumps. I don&apos;t FOMO. I don&apos;t panic sell.<br/>
                I just swim. For 399 years. In complete darkness.<br/>
                At temperatures that would freeze your hardware wallet.
              </p>
            </div>
            
            <p className="text-center text-red-600 font-bold text-sm md:text-base">
              THIS IS NOT A MEME. THIS IS A LIFESTYLE. THIS IS THE WAY.
            </p>
          </div>
        </Win95Window>
        
        {/* Meme Image */}
        <div className="mt-8 flex justify-center">
          <Win95Window title="the_deep_lore.jpg" className="max-w-lg w-full">
            <img 
              src={IMAGES.memeLore} 
              alt="The Deep Lore" 
              className="w-full"
              data-testid="about-meme"
            />
          </Win95Window>
        </div>
      </div>
    </section>
  );
};

// Tokenomics Section (BSOD Style)
const TokenomicsSection = () => {
  return (
    <section id="tokenomics" className="bsod-section relative scanlines" data-testid="tokenomics-section">
      <div className="bsod-title">$SHARK TOKENOMICS</div>
      
      <div className="bsod-text space-y-4 md:space-y-6 px-4">
        <p className="text-sm md:text-base">A problem has been detected and your fiat has been shut down to prevent further losses.</p>
        
        <div className="text-left space-y-2 md:space-y-4 max-w-md mx-auto text-xs md:text-base">
          <p>TOTAL_SUPPLY.......... 1,000,000,000 $SHARK</p>
          <p>TAX_RATE.............. ERROR 404: NOT FOUND</p>
          <p>LP_STATUS............. BURNED (FOREVER)</p>
          <p>TEAM_ALLOCATION....... 0% (COMMUNITY ONLY)</p>
          <p>RUG_PROBABILITY....... 0x00000000</p>
        </div>
        
        <div className="mt-6 md:mt-8 p-3 md:p-4 border-2 border-white">
          <p className="blink text-xs md:text-sm">
            Technical information:<br/>
            *** STOP: 0x0000SHARK (0xFFFFFFFF, 0x00000000, 0xDIAMONDFINS)
          </p>
        </div>
        
        <p className="text-xs md:text-sm opacity-75">
          Beginning dump of physical memory...<br/>
          Physical memory dump complete.<br/>
          Contact your nearest DEX for further assistance.
        </p>
      </div>
      
      {/* BSOD Meme */}
      <div className="mt-6 md:mt-8 max-w-lg mx-auto px-4">
        <img 
          src={IMAGES.memeBsod} 
          alt="BSOD Meme" 
          className="w-full border-4 border-white"
          data-testid="tokenomics-meme"
        />
      </div>
    </section>
  );
};

// How to Buy Section (Setup Wizard)
const HowToBuySection = () => {
  const [step, setStep] = useState(1);
  const { playClick } = useAudio() || {};
  
  const steps = [
    {
      title: "GET A WALLET",
      description: "Download Phantom or any Solana wallet. It's like a digital aquarium for your $SHARK.",
      link: "https://phantom.app/",
      linkText: "Download Phantom"
    },
    {
      title: "GET SOME SOL",
      description: "Buy SOL from any exchange (Coinbase, Binance, etc.) and send it to your wallet.",
      link: null,
      linkText: null
    },
    {
      title: "SWAP FOR $SHARK",
      description: "Go to Raydium or Jupiter, paste the contract address, and swap your SOL for $SHARK!",
      link: SOCIALS.raydium,
      linkText: "Go to Raydium"
    }
  ];

  const handleStepChange = (newStep) => {
    playClick?.();
    setStep(newStep);
  };

  return (
    <section id="howtobuy" className="section-spacing bg-purple-900" data-testid="howtobuy-section">
      <div className="max-w-2xl mx-auto px-4">
        <Win95Window title="$SHARK Setup Wizard" className="setup-wizard">
          <div className="md:flex">
            {/* Sidebar */}
            <div className="wizard-sidebar md:w-1/3">
              <img src={IMAGES.logo} alt="$SHARK" className="w-16 md:w-20 h-16 md:h-20 mb-4 mx-auto md:mx-0" />
              <p className="font-pixel text-xs">How to buy $SHARK</p>
              <p className="text-sm mt-2 opacity-75">Setup Wizard v3.99</p>
            </div>
            
            {/* Content */}
            <div className="wizard-content md:w-2/3">
              <div className="mb-4">
                <p className="font-pixel text-xs md:text-sm mb-2">Step {step} of 3</p>
                <div className="flex gap-1">
                  {[1, 2, 3].map((s) => (
                    <div 
                      key={s}
                      className={`h-2 flex-1 ${s <= step ? 'bg-blue-600' : 'bg-gray-400'}`}
                    />
                  ))}
                </div>
              </div>
              
              <h3 className="font-pixel text-xs md:text-sm mb-3">{steps[step - 1].title}</h3>
              <p className="font-terminal text-sm mb-4">{steps[step - 1].description}</p>
              
              {steps[step - 1].link && (
                <RetroButton 
                  href={steps[step - 1].link} 
                  target="_blank"
                  className="mb-4"
                  data-testid={`step-${step}-link`}
                >
                  <ExternalLink size={12} className="inline mr-1" />
                  {steps[step - 1].linkText}
                </RetroButton>
              )}
              
              <div className="flex justify-between mt-6 pt-4 border-t-2 border-gray-400">
                <RetroButton 
                  onClick={() => handleStepChange(Math.max(1, step - 1))}
                  disabled={step === 1}
                  className={step === 1 ? 'opacity-50' : ''}
                  data-testid="prev-step-btn"
                >
                  &lt; Back
                </RetroButton>
                {step < 3 ? (
                  <RetroButton 
                    onClick={() => handleStepChange(Math.min(3, step + 1))}
                    data-testid="next-step-btn"
                  >
                    Next &gt;
                  </RetroButton>
                ) : (
                  <RetroButton 
                    href={SOCIALS.raydium} 
                    target="_blank"
                    className="bg-green-500"
                    data-testid="finish-btn"
                  >
                    <Wallet size={12} className="inline mr-1" />
                    BUY NOW!
                  </RetroButton>
                )}
              </div>
            </div>
          </div>
        </Win95Window>
      </div>
    </section>
  );
};

// Meme Gallery Section
const MemeGallery = () => {
  const memes = [
    { src: IMAGES.memeLore, title: "the_deep_lore.jpg" },
    { src: IMAGES.memeNetflix, title: "netflix_doc.jpg" },
    { src: IMAGES.memeBsod, title: "system_error.jpg" },
    { src: IMAGES.memeForum, title: "forum_2003.jpg" }
  ];

  return (
    <section id="memes" className="section-spacing" style={{ background: '#008080' }} data-testid="memes-section">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="font-pixel text-lg md:text-xl text-white neon-green">MEME VAULT</h2>
          <p className="font-terminal text-white opacity-75 mt-2 text-sm md:text-base">399 years of meme wisdom</p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {memes.map((meme, index) => (
            <Win95Window key={index} title={meme.title} className="meme-card">
              <img 
                src={meme.src} 
                alt={meme.title}
                className="w-full"
                data-testid={`meme-${index}`}
              />
            </Win95Window>
          ))}
        </div>
        
        <div className="text-center mt-8">
          <RetroButton 
            href={SOCIALS.twitter} 
            target="_blank"
            data-testid="share-memes-btn"
          >
            <Twitter size={14} className="inline mr-2" />
            SHARE YOUR MEMES
          </RetroButton>
        </div>
      </div>
    </section>
  );
};

// Footer / Start Bar
const StartBar = ({ onStartClick, scrollToSection, musicPlaying, onMusicToggle, soundEnabled, onSoundToggle, onOpenAbout }) => {
  const [time, setTime] = useState(new Date());
  const [startMenuOpen, setStartMenuOpen] = useState(false);
  const { playClick } = useAudio() || {};
  
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleStartClick = () => {
    playClick?.();
    setStartMenuOpen(!startMenuOpen);
  };

  return (
    <>
      <StartMenu 
        isOpen={startMenuOpen} 
        onClose={() => setStartMenuOpen(false)}
        scrollToSection={scrollToSection}
        onOpenAbout={onOpenAbout}
      />
      {/* Desktop Taskbar */}
      <footer className="start-bar fixed bottom-0 left-0 right-0 z-50 hidden md:block" data-testid="start-bar">
        <div className="flex items-center justify-between max-w-7xl mx-auto p-2">
          {/* Start Button & Links */}
          <div className="flex items-center gap-2">
            <RetroButton 
              onClick={handleStartClick} 
              className={`flex items-center gap-2 ${startMenuOpen ? 'border-t-gray-800 border-l-gray-800 border-b-white border-r-white' : ''}`}
              data-testid="start-btn"
            >
              <img src={IMAGES.logo} alt="$SHARK" className="w-5 h-5" />
              <span>Start</span>
            </RetroButton>
            
            <div className="h-6 w-px bg-gray-500" />
            
            <RetroButton href={SOCIALS.twitter} target="_blank" className="social-btn">
              <Twitter size={14} />
              <span className="ml-1">Twitter</span>
            </RetroButton>
            
            <RetroButton href={SOCIALS.telegram} target="_blank" className="social-btn">
              <Send size={14} />
              <span className="ml-1">Telegram</span>
            </RetroButton>
            
            <RetroButton href={SOCIALS.dexscreener} target="_blank" className="social-btn">
              <BarChart3 size={14} />
              <span className="ml-1">Chart</span>
            </RetroButton>
          </div>
          
          {/* Right side - Audio Controls & Clock */}
          <div className="flex items-center gap-2">
            <RetroButton 
              onClick={onSoundToggle}
              className="px-2"
              title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
              data-testid="sound-toggle"
            >
              {soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </RetroButton>
            
            <RetroButton 
              onClick={onMusicToggle}
              className={`px-2 ${musicPlaying ? 'bg-green-300' : ''}`}
              title={musicPlaying ? 'Stop music' : 'Play ambient music'}
              data-testid="music-toggle"
            >
              <Fish size={14} className={musicPlaying ? 'animate-pulse' : ''} />
            </RetroButton>
            
            <div className="win-button text-xs font-terminal px-3" data-testid="clock">
              {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile Taskbar - Simplified & Clean */}
      <footer className="mobile-taskbar fixed bottom-0 left-0 right-0 z-50 md:hidden" data-testid="mobile-taskbar">
        <div className="flex items-center justify-around py-2 px-1">
          {/* Start */}
          <button 
            onClick={handleStartClick}
            className={`mobile-taskbar-btn ${startMenuOpen ? 'active' : ''}`}
            data-testid="mobile-start-btn"
          >
            <img src={IMAGES.logo} alt="" className="w-6 h-6" />
          </button>
          
          {/* Twitter */}
          <a href={SOCIALS.twitter} target="_blank" rel="noopener noreferrer" className="mobile-taskbar-btn">
            <Twitter size={20} />
          </a>
          
          {/* Telegram */}
          <a href={SOCIALS.telegram} target="_blank" rel="noopener noreferrer" className="mobile-taskbar-btn">
            <Send size={20} />
          </a>
          
          {/* Chart */}
          <a href={SOCIALS.dexscreener} target="_blank" rel="noopener noreferrer" className="mobile-taskbar-btn">
            <BarChart3 size={20} />
          </a>
          
          {/* Sound */}
          <button onClick={onSoundToggle} className="mobile-taskbar-btn">
            {soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
          </button>
          
          {/* Music */}
          <button 
            onClick={onMusicToggle} 
            className={`mobile-taskbar-btn ${musicPlaying ? 'active' : ''}`}
          >
            <Fish size={20} className={musicPlaying ? 'animate-pulse' : ''} />
          </button>
        </div>
      </footer>
    </>
  );
};

// Main App
function App() {
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [easterEggMessage, setEasterEggMessage] = useState(null);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  
  const soundEffects = useSoundEffects();

  const fetchTokenData = useCallback(async () => {
    try {
      const response = await fetch(DEX_API);
      const data = await response.json();
      if (data.pair) {
        setTokenData(data.pair);
      }
    } catch (error) {
      console.error('Failed to fetch token data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokenData();
    const interval = setInterval(fetchTokenData, 30000);
    return () => clearInterval(interval);
  }, [fetchTokenData]);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const triggerEasterEgg = useCallback(() => {
    const randomMessage = EASTER_EGGS[Math.floor(Math.random() * EASTER_EGGS.length)];
    setEasterEggMessage(randomMessage);
    soundEffects.playEasterEgg();
  }, [soundEffects]);

  const handleMusicToggle = () => {
    soundEffects.playClick();
    setMusicPlaying(!musicPlaying);
  };

  const handleSoundToggle = () => {
    soundEffects.setSoundEnabled(!soundEffects.soundEnabled);
  };

  return (
    <AudioContext.Provider value={soundEffects}>
      <div className="App pb-16" data-testid="app-container">
        {/* Background Music */}
        <BackgroundMusic isPlaying={musicPlaying} volume={0.08} />
        
        {/* Easter Egg Toast */}
        {easterEggMessage && (
          <EasterEggToast 
            message={easterEggMessage} 
            onClose={() => setEasterEggMessage(null)} 
          />
        )}
        
        {/* About Dialog */}
        <AboutDialog isOpen={aboutOpen} onClose={() => setAboutOpen(false)} />
        
        {/* Price Ticker */}
        <PriceTicker tokenData={tokenData} loading={loading} />
        
        {/* Hero */}
        <HeroSection scrollToSection={scrollToSection} onEasterEgg={triggerEasterEgg} />
        
        {/* Marquee */}
        <MarqueeBanner />
        
        {/* About */}
        <AboutSection onEasterEgg={triggerEasterEgg} />
        
        {/* Tokenomics */}
        <TokenomicsSection />
        
        {/* How to Buy */}
        <HowToBuySection />
        
        {/* Meme Gallery */}
        <MemeGallery />
        
        {/* Second Marquee */}
        <MarqueeBanner />
        
        {/* Start Bar Footer */}
        <StartBar 
          scrollToSection={scrollToSection}
          musicPlaying={musicPlaying}
          onMusicToggle={handleMusicToggle}
          soundEnabled={soundEffects.soundEnabled}
          onSoundToggle={handleSoundToggle}
          onOpenAbout={() => setAboutOpen(true)}
        />
      </div>
    </AudioContext.Provider>
  );
}

export default App;
